import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconArrowBackUp, IconMessageExclamation } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ReworkNote } from '../../hooks/useEssayApi';

const MAX_COMMENT = 2000;

/**
 * Кнопка возврата автору на доработку. Комментарий обязателен: возврат без
 * объяснения читается как «переделай, а что именно — угадай». Кнопка в модалке
 * неактивна, пока поле пустое, но решает всё равно сервер — форма тут только
 * подсказывает.
 */
export const ReturnForReworkButton: React.FC<{
  onReturn: (comment: string) => Promise<unknown>;
  isPending?: boolean;
}> = ({ onReturn, isPending }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');

  // Модалку закрываем только после успеха: сервер может отказать (не сдано,
  // права разошлись), и тогда человек должен видеть свой текст, а не пустой
  // экран. Ошибку показывает мутация тостом — здесь её достаточно проглотить,
  // иначе await бросит наружу необработанным промисом.
  const submit = async () => {
    try {
      await onReturn(comment.trim());
      setIsOpen(false);
      setComment('');
    } catch {
      /* уже показано тостом */
    }
  };

  return (
    <>
      <Button
        variant="light"
        color="orange"
        leftSection={<IconArrowBackUp size={16} />}
        onClick={() => setIsOpen(true)}
      >
        {t('rework.action')}
      </Button>
      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('rework.modalTitle')}
        centered
      >
        <Stack>
          <Textarea
            label={t('rework.commentLabel')}
            description={t('rework.commentHint')}
            placeholder={t('rework.commentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            maxLength={MAX_COMMENT}
            autosize
            minRows={3}
            maxRows={10}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setIsOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              color="orange"
              onClick={() => void submit()}
              loading={isPending}
              disabled={!comment.trim()}
            >
              {t('rework.action')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

/**
 * Плашка автору: документ вернули, вот причина. Без неё человек видит «черновик»
 * и не понимает, сам не досдал или от него чего-то ждут.
 */
export const ReturnedNotice: React.FC<{ note?: ReworkNote | null }> = ({ note }) => {
  const { t, i18n } = useTranslation();
  if (!note) return null;

  // `by` приходит подгруженным объектом, но у старых записей может остаться
  // голым id — тогда просто не называем имени.
  const authorName = typeof note.by === 'object' ? note.by?.name : undefined;

  return (
    <Alert color="orange" variant="light" icon={<IconMessageExclamation size={16} />}>
      <Text size="sm" fw={600} mb={4}>
        {authorName
          ? t('rework.noticeBy', {
              name: authorName,
              date: dayjs(note.at)
                .locale(i18n.language || 'ru')
                .format('D MMMM'),
            })
          : t('rework.notice', {
              date: dayjs(note.at)
                .locale(i18n.language || 'ru')
                .format('D MMMM'),
            })}
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {note.comment}
      </Text>
    </Alert>
  );
};
